/**
 * The upload jobs of this tab (ANON-04, UPL-04). They run here, outside any page, while the user
 * works anywhere in the platform, until the tab is closed. A job is checked as soon as a token is
 * set; confirmed jobs send one at a time, oldest first, so the uplink serves one job and the next
 * starts when its files are all sent.
 */
import type { Overrides, Setup } from '../anonymization/engine'
import type { EnginePool } from '../anonymization/pool'
import type { LoadStage } from '../anonymization/protocol'
import { UploadJob, WORKING, type JobContext, type JobView, type Phase } from './job'
import { referringTo } from './pipeline'
import { SentFiles } from './sent'
import { expandArchives } from './zip'

/** The site token the jobs anonymize with, normalized, with its key ID and its site (TOK-07). */
export interface UploadToken {
  /** The token with the site's configuration, one object for as long as neither changes. */
  setup: Setup
  keyId: string
  siteId: string
}

export interface UploadsState {
  readonly jobs: readonly JobView[]
  /** How many engines work at once. */
  readonly engines: number
  /** How far the engines' start has come, while they load. */
  readonly stage?: LoadStage
}

/** Jobs that must be checked again under another token: checked, but nothing sent with it yet. */
const RECHECK: ReadonlySet<Phase> = new Set(['checked', 'queued', 'paused'])

export class UploadJobs {
  private jobs: UploadJob[] = []
  private state: UploadsState
  private readonly listeners = new Set<() => void>()
  private token: UploadToken | null = null
  private sentFiles: SentFiles | undefined

  constructor(
    private readonly userId: string,
    readonly engines: EnginePool,
  ) {
    this.state = { jobs: [], engines: engines.engines }
  }

  readonly subscribe = (listener: () => void) => {
    this.listeners.add(listener)
    return () => void this.listeners.delete(listener)
  }

  readonly snapshot = () => this.state

  /** A new job for the chosen files, archives replaced by their files (ANON-04); null if none was chosen. */
  async add(files: readonly File[]): Promise<UploadJob | null> {
    const sources = await expandArchives(files)
    if (sources.length === 0) return null
    const job = new UploadJob(sources)
    job.subscribe(this.jobChanged)
    this.jobs = [job, ...this.jobs]
    this.publish()
    this.checkNew()
    return job
  }

  /** Removes a job that does not work, wait or hold files in hand. */
  remove(id: string): void {
    const job = this.find(id)
    if (!job || WORKING.has(job.phase) || job.phase === 'queued') return
    job.close()
    this.jobs = this.jobs.filter((candidate) => candidate !== job)
    this.publish()
  }

  /**
   * The token set at the navigation's foot, or null; files checked with another one, or before the
   * site's configuration was set or changed (TOK-28, TOK-29), are checked again.
   */
  setToken(token: UploadToken | null): void {
    // The same token and site configuration keep their setup, so the engines that hold it keep working.
    const same = (setup?: Setup) => setup?.token === this.token?.setup.token && JSON.stringify(setup?.site) === JSON.stringify(this.token?.setup.site)
    if (same(token?.setup)) return
    const previous = this.token
    this.token = token
    if (!token) return this.engines.close()
    const siteChanged = previous !== null && JSON.stringify(previous.setup.site) !== JSON.stringify(token.setup.site)
    for (const job of this.jobs) {
      const unsent = job.phase === 'done' && job.snapshot().tally.counts.ready > 0
      if (job.keyId !== null && (job.keyId !== token.keyId || siteChanged) && (RECHECK.has(job.phase) || unsent)) job.restart()
    }
    this.checkNew()
    this.pump()
  }

  /** Checks a job again, for example after its check failed. */
  check(id: string): void {
    const job = this.find(id)
    if (job && this.token && !WORKING.has(job.phase)) void job.check(this.context(this.token))
  }

  correct(id: string, keys: ReadonlySet<string>, overrides: Overrides): void {
    const job = this.find(id)
    if (job && this.token && !WORKING.has(job.phase)) void job.correct(this.context(this.token), keys, overrides)
  }

  leaveOut(id: string, keys: ReadonlySet<string>, reason?: string): void {
    const job = this.find(id)
    job?.leaveOut(keys, reason)
    this.recheckReferring(job, keys)
  }

  keepIn(id: string, keys: ReadonlySet<string>): void {
    const job = this.find(id)
    job?.keepIn(keys)
    this.recheckReferring(job, keys)
  }

  /** Checks again the files that refer to files left out or taken back: their references resolve differently (References). */
  private recheckReferring(job: UploadJob | undefined, keys: ReadonlySet<string>): void {
    if (!job || !this.token || WORKING.has(job.phase)) return
    const referring = referringTo(keys, job.snapshot().items)
    if (referring.size > 0) void job.recheck(this.context(this.token), referring)
  }

  /** Queues a checked or paused job, after the uploader confirmed both statements (UPL-02). */
  upload(id: string): void {
    const job = this.find(id)
    if (!job || WORKING.has(job.phase) || job.phase === 'queued') return
    job.queue()
    this.pump()
  }

  /** Pauses an upload, cancels a check, or takes a job out of the queue. */
  pause(id: string): void {
    const job = this.find(id)
    if (job?.phase === 'queued') job.unqueue()
    else job?.pause()
  }

  /** Changes how many engines work at once, also for the work under way. */
  resizeEngines(count: number): void {
    this.engines.resize(count)
    this.state = { ...this.state, engines: this.engines.engines }
    this.notify()
  }

  /** Tells the pages how far the engines' start has come. */
  readonly stageChanged = (stage: LoadStage) => {
    this.state = { ...this.state, stage }
    this.notify()
  }

  close(): void {
    for (const job of this.jobs) job.close()
    this.engines.close()
  }

  private find(id: string): UploadJob | undefined {
    return this.jobs.find((job) => job.id === id)
  }

  /** Checks the jobs waiting for a token; those whose check failed wait for the user. */
  private checkNew(): void {
    if (!this.token) return
    for (const job of this.jobs) {
      if (job.phase === 'new' && !job.snapshot().error) void job.check(this.context(this.token))
    }
  }

  /** Starts the oldest queued job once no other one sends. */
  private pump(): void {
    if (!this.token || this.jobs.some((job) => job.phase === 'uploading')) return
    void this.jobs.findLast((job) => job.phase === 'queued')?.send(this.context(this.token))
  }

  private readonly jobChanged = () => {
    this.publish()
    this.pump()
  }

  private publish(): void {
    this.state = { ...this.state, jobs: this.jobs.map((job) => job.snapshot()) }
    this.notify()
  }

  private notify(): void {
    for (const listener of this.listeners) listener()
  }

  private context(token: UploadToken): JobContext {
    // The files this browser sent (UPL-04).
    this.sentFiles ??= new SentFiles(this.userId)
    return { engines: this.engines, ...token, sent: this.sentFiles }
  }
}

/** The job that matters most right now, for the navigation and the dock: one at work, then one waiting for the user. */
export function leadingJob(jobs: readonly JobView[]): JobView | undefined {
  const rank = (job: JobView) =>
    job.phase === 'uploading'
      ? 0
      : job.phase === 'checking'
        ? 1
        : job.phase === 'finishing'
          ? 2
          : job.phase === 'queued'
            ? 3
            : job.phase === 'paused'
              ? 4
              : job.phase === 'checked' || job.phase === 'new'
                ? 5
                : 6
  return [...jobs].sort((a, b) => rank(a) - rank(b))[0]
}
